import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useQuery } from '@tanstack/react-query';

import { CtaButton, TextInput, CheckboxInput, FullPageLoader } from '@tassomutuo-backoffice/ui';
import { CappelloParlanteApi } from '../../../../services/api/cappelloparlante/cappelloparlante';

export interface LeadChecked {
  leadId?: string;
  bestId: string;
  distance: number;
  consultantName?: string;
  checkAmbient: string;
}

export interface CappelloParlanteSectionProps {
  title: string;
  api: CappelloParlanteApi;
  defaultExpanded?: boolean;
}

const CappelloParlanteSection = ({ title, api, defaultExpanded = false }: CappelloParlanteSectionProps) => {
  const { isPending, data, error } = useQuery({
    queryKey: ['getCurrentCappelloParlanteVersion', title],
    queryFn: api.getCurrentVersion,
  });

  const [versionToShow, setVersionToShow] = useState<string>('- not available');

  useEffect(() => {
    if (data && data.length > 0) {
      setVersionToShow(data);
    }
  }, [data]);

  const [leadId, setLeadId] = useState('');
  const [isTraining, setIsTraining] = useState(false);
  const [checkAmbient, setCheckAmbient] = useState('stage');
  const [leadResults, setLeadResults] = useState<LeadChecked[]>([]);
  const [loadingLead, setLoadingLead] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);

  const handleLeadSearch = async () => {
    setLoadingLead(true);
    setLeadError(null);
    try {
      const respBestId = await api.getBestId(leadId, checkAmbient === 'stage');
      setLeadResults((prev) => [
        ...prev,
        {
          leadId: respBestId.lead_id ?? leadId,
          bestId: respBestId.best_id,
          distance: respBestId.distance,
          consultantName: respBestId.consultant_name,
          checkAmbient,
        },
      ]);
    } catch (err: unknown) {
      setLeadError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingLead(false);
    }
  };

  useEffect(() => {
    const checkRetrainingStatus = async () => {
      const isRetraining = await api.isTrainingInProgress();
      if (!isRetraining) {
        setIsTraining(false);
      }
    };

    if (isTraining) {
      const interval = setInterval(checkRetrainingStatus, 1000);
      return () => clearInterval(interval);
    }
  }, [isTraining, api]);

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" fontWeight="bold">
          {title} {versionToShow}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FullPageLoader show={isPending || isTraining || loadingLead} />

        {!isPending && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message ? `Error: ${error.message}` : 'An Error occurred'}
          </Alert>
        )}

        <Box display="flex" gap={2} mb={2}>
          <CtaButton
            variant="contained"
            onClick={() => {
              api.retrainStage();
              setIsTraining(true);
            }}
          >
            <AutoFixHighIcon sx={{ mr: 1 }} fontSize="small" />
            Retrain Stage
          </CtaButton>
          <CtaButton
            variant="contained"
            color="success"
            onClick={() => {
              api.retrainProduction();
              setIsTraining(true);
            }}
          >
            <AutoFixHighIcon sx={{ mr: 1 }} fontSize="small" />
            Retrain Produzione
          </CtaButton>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleLeadSearch();
          }}
        >
          <Box flex={1}>
            <TextInput
              placeholder="Inserisci ID Lead"
              value={leadId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeadId(e.target.value)}
              disabled={loadingLead}
            />
          </Box>
          <CheckboxInput
            label="Produzione"
            checked={checkAmbient === 'production'}
            onChange={() => setCheckAmbient('production')}
            disabled={loadingLead}
          />
          <CheckboxInput
            label="Stage"
            checked={checkAmbient === 'stage'}
            onChange={() => setCheckAmbient('stage')}
            disabled={loadingLead}
          />
          <CtaButton type="submit" variant="contained" disabled={loadingLead || !leadId}>
            {loadingLead ? 'mmm... sto pensando...' : 'Chiedi al Cappello Parlante'}
          </CtaButton>
        </Box>

        {leadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {leadError}
          </Alert>
        )}

        {leadResults.length > 0 && (
          <Box display="flex" flexDirection="column" mt={3}>
            {[...leadResults].reverse().map((result, idx) => (
              <Box key={idx} display="flex" flexDirection="row" mb={2} p={2} bgcolor="white" borderRadius={1}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Risultato {result.leadId} [{result.checkAmbient}]:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {result.consultantName ? `${result.consultantName} ` : ''}[{result.bestId}]
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Distanza: {result.distance} km
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CappelloParlanteSection;
